import type { ArenaDef, TileMap } from '@archon3d/core';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { ArenaFog } from './arenaFog';
import { ArenaLamps, lampsOf } from './arenaLamps';
import { createTrees } from './arenaProps';
import { LiquidSurfaces } from './liquidSurfaces';

/** What an arena's data draws beyond its blocks: trees, liquid surfaces, fog banks and lamps. */
export interface ArenaDressing {
  trees: ReturnType<typeof createTrees>;
  liquids: LiquidSurfaces;
  fog: ArenaFog;
  lamps: ArenaLamps;
  /** The meshes that never move on the floor, whose world matrices can be frozen. */
  still: Mesh[];
  dispose(): void;
}

/**
 * Dresses an arena as its data describes (content:quality#2, x-aaa-gap#10), under `root` beside
 * the blocks `arenaMesh.ts` builds: the duel's stage and the arena editor's preview both use it,
 * so the preview shows what a duel will. `room` is what the lamps must not light.
 */
export function dressArena(
  scene: Scene,
  root: TransformNode,
  arena: ArenaDef,
  map: TileMap,
  room: readonly AbstractMesh[] = [],
): ArenaDressing {
  const trees = createTrees(scene, root, map);
  const liquids = new LiquidSurfaces(scene, root, map);
  const fog = new ArenaFog(scene, root, arena.fog);
  const lamps = new ArenaLamps(scene, root, lampsOf(map), room);
  return {
    trees,
    liquids,
    fog,
    lamps,
    still: [...(trees ? [trees.mesh] : []), ...liquids.meshes],
    dispose() {
      lamps.dispose();
      trees?.dispose();
      liquids.dispose();
      fog.dispose();
    },
  };
}
